<template>
    <div class="portal">
        <!-- Decorative corner shapes, purely visual. Mirrors LoginUser so the
             two pages read as one flow. -->
        <div class="deco deco-blue" aria-hidden="true"></div>
        <div class="deco deco-pink" aria-hidden="true"></div>

        <div class="shell">
            <!-- Language switch -->
            <div class="lang-row">
                <div class="lang-toggle" role="group" aria-label="Language">
                    <button v-for="code in locales" :key="code" type="button" class="lang-btn"
                        :class="{ active: locale === code }" :aria-pressed="locale === code"
                        @click="setLocale(code)">
                        {{ code.toUpperCase() }}
                    </button>
                </div>
            </div>

            <header class="hero">
                <img class="hero-logo" src="img/logo.png" alt="WashXpress" />
            </header>

            <div class="card-form">
                <div class="success">
                    <span class="success-badge" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="34" height="34">
                            <path fill="none" stroke="#ffffff" stroke-width="2.6"
                                stroke-linecap="round" stroke-linejoin="round"
                                d="M4.5 12.8l4.8 4.7L19.5 7.2" />
                        </svg>
                    </span>
                    <h1 class="success-title">{{ t('statusTitle') }}</h1>
                    <p class="success-sub">{{ t('statusSubtitle') }}</p>
                </div>

                <dl v-if="hasSession" class="details">
                    <div v-if="username" class="details-row">
                        <dt>{{ t('statusAccount') }}</dt>
                        <dd class="mono">{{ username }}</dd>
                    </div>
                    <div v-if="ip" class="details-row">
                        <dt>{{ t('statusIp') }}</dt>
                        <dd class="mono">{{ ip }}</dd>
                    </div>
                    <div v-if="mac" class="details-row">
                        <dt>{{ t('statusMac') }}</dt>
                        <dd class="mono">{{ mac }}</dd>
                    </div>
                </dl>

                <p v-else class="msg msg-hint">{{ t('statusNoSession') }}</p>

                <a class="btn-primary" :class="{ 'is-busy': checking }" :href="actionHref"
                    :aria-disabled="checking ? 'true' : 'false'" @click="onActionClick">
                    {{ actionLabel }}
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { LOCALES, detectLocale, persistLocale, translate } from "@/i18n";
import {
    CONNECTIVITY_PROBE_TIMEOUT_MS,
    CONNECTIVITY_PROBE_URL,
    FACEBOOK_PAGE_URL,
} from "@/config";

export default {
    name: "StatusUser",
    data() {
        return {
            locale: detectLocale(),
            locales: LOCALES,

            // null while the probe is in flight, then true/false. The button
            // target depends on this, so it starts in the "checking" state
            // rather than guessing.
            online: null,
            probeTimer: null,
            probeImage: null,
        };
    },
    computed: {
        // Mikrotik's alogin.html submits these to /status after a successful
        // login. See washxpress_hotspot/alogin.html for the full field list.
        username() {
            return this.$route.query.username || "";
        },
        ip() {
            return this.$route.query.ip || "";
        },
        mac() {
            return this.$route.query.mac || "";
        },
        hasSession() {
            return !!(this.username || this.ip || this.mac);
        },
        /**
         * Where to go when the client is NOT online. Defaults to the portal
         * root: opened directly there is no link-login-only to go back to, and
         * the bare binding produced an href of "undefined".
         */
        backHref() {
            return this.$route.query["link-login-only"] || "/";
        },
        checking() {
            return this.online === null;
        },
        /**
         * Going "back" to the login page is a dead end for someone who is
         * already online, so once the probe confirms real internet access the
         * button becomes a link to the WashXpress page instead.
         *
         * While the probe is still running this stays on backHref, so a click
         * that beats the probe behaves the way it always did rather than
         * opening a page the client may not be able to reach.
         */
        actionHref() {
            return this.online === true ? FACEBOOK_PAGE_URL : this.backHref;
        },
        actionLabel() {
            if (this.checking) return this.t("statusChecking");
            return this.online ? this.t("statusFacebook") : this.t("statusBack");
        },
    },
    mounted() {
        this.probeConnectivity();
    },
    beforeUnmount() {
        this.cancelProbe();
    },
    methods: {
        t(key, params) {
            return translate(this.locale, key, params);
        },
        setLocale(code) {
            this.locale = code;
            persistLocale(code);
        },

        /**
         * Loads an image over HTTPS and treats a successful decode as proof of
         * internet access. See CONNECTIVITY_PROBE_URL in config.js for why the
         * probe has to be HTTPS and has to point at a host outside the walled
         * garden.
         *
         * An <img> is used rather than fetch() on purpose: a cross-origin
         * fetch would need CORS headers the probe host does not send, and
         * `mode: "no-cors"` returns an opaque response that resolves even when
         * the hotspot answered instead of the real host. An image either
         * decodes or fires onerror, which is the signal we actually want.
         */
        probeConnectivity() {
            this.cancelProbe();

            const img = new Image();
            this.probeImage = img;

            const settle = (result) => {
                if (this.probeImage !== img) return; // superseded or cancelled
                clearTimeout(this.probeTimer);
                this.probeTimer = null;
                this.probeImage = null;
                img.onload = null;
                img.onerror = null;
                this.online = result;
            };

            this.probeTimer = setTimeout(() => settle(false), CONNECTIVITY_PROBE_TIMEOUT_MS);
            img.onload = () => settle(true);
            img.onerror = () => settle(false);

            // Cache buster: a hit cached from a previous visit would report
            // success without touching the network.
            const sep = CONNECTIVITY_PROBE_URL.includes("?") ? "&" : "?";
            img.src = `${CONNECTIVITY_PROBE_URL}${sep}_=${Date.now()}`;
        },
        cancelProbe() {
            if (this.probeTimer) {
                clearTimeout(this.probeTimer);
                this.probeTimer = null;
            }
            if (this.probeImage) {
                this.probeImage.onload = null;
                this.probeImage.onerror = null;
                this.probeImage = null;
            }
        },
        /** Swallows clicks while the probe decides which target applies. */
        onActionClick(event) {
            if (this.checking) event.preventDefault();
        },
    },
};
</script>

<style scoped>
/* Shares the shell, decorations, button and message styling with LoginUser so
   the post-login page does not look like a different product. */
.portal {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    background: #eef1f9;
    font-family: "Noto Sans Thai", "Prompt", -apple-system, BlinkMacSystemFont, "Segoe UI",
        sans-serif;
}

/* Decorative corner shapes */
.deco {
    position: absolute;
    pointer-events: none;
}

.deco-blue {
    top: -90px;
    left: -110px;
    width: 230px;
    height: 230px;
    background: #204496;
    border-radius: 0 0 100% 0;
    transform: rotate(-12deg);
}

.deco-pink {
    top: 40px;
    left: -70px;
    width: 180px;
    height: 180px;
    border: 26px solid #f7a8bb;
    border-radius: 50%;
    border-right-color: transparent;
    border-top-color: transparent;
}

.shell {
    position: relative;
    z-index: 1;
    max-width: 420px;
    margin: 0 auto;
    padding: 16px 24px 40px;
}

/* Language switch */
.lang-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 28px;
}

.lang-toggle {
    display: inline-flex;
    background: #ffffff;
    border-radius: 999px;
    padding: 4px;
    box-shadow: 0 2px 6px rgba(32, 68, 150, 0.12);
}

.lang-btn {
    min-width: 54px;
    padding: 8px 14px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #6b7694;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
}

.lang-btn.active {
    background: #204496;
    color: #ffffff;
}

.lang-btn:focus-visible {
    outline: 2px solid #204496;
    outline-offset: 2px;
}

/* Hero */
.hero {
    text-align: center;
    margin-bottom: 24px;
}

.hero-logo {
    max-width: 200px;
    width: 62%;
    height: auto;
}

.card-form {
    text-align: center;
}

/* Success block */
.success {
    margin-bottom: 20px;
}

.success-badge {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 76px;
    margin-bottom: 16px;
    border-radius: 50%;
    background: #204496;
    box-shadow: 0 6px 16px rgba(32, 68, 150, 0.28);
}

/* Pink ring echoing the hero decoration on the login page. */
.success-badge::after {
    content: "";
    position: absolute;
    inset: -9px;
    border: 3px solid #f7a8bb;
    border-radius: 50%;
}

.success-title {
    margin: 0 0 8px;
    color: #204496;
    font-size: 25px;
    font-weight: 800;
    line-height: 1.3;
}

.success-sub {
    margin: 0;
    color: #3f4a66;
    font-size: 16px;
    font-weight: 600;
}

/* Session details */
.details {
    margin: 0 0 26px;
    padding: 6px 18px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(32, 68, 150, 0.1);
    text-align: left;
}

.details-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 13px 0;
    border-bottom: 1px solid #eef1f9;
}

.details-row:last-child {
    border-bottom: 0;
}

.details dt {
    flex: 0 0 auto;
    color: #6b7694;
    font-size: 14px;
    font-weight: 600;
}

.details dd {
    margin: 0;
    min-width: 0;
    color: #20304f;
    font-size: 15px;
    font-weight: 700;
    text-align: right;
    overflow-wrap: anywhere;
}

.mono {
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.2px;
}

/* Messages */
.msg {
    margin: 0 0 26px;
    font-size: 15px;
    line-height: 1.6;
}

.msg-hint {
    color: #204496;
    font-weight: 600;
}

/* Button */
.btn-primary {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 56px;
    border: 0;
    border-radius: 999px;
    background: #204496;
    color: #ffffff;
    font-size: 18px;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.2s ease;
}

.btn-primary:hover,
.btn-primary:focus-visible {
    background: #16327a;
    color: #ffffff;
}

.btn-primary:focus-visible {
    outline: 2px solid #16327a;
    outline-offset: 2px;
}

/* While the connectivity probe decides which target the button should use. */
.btn-primary.is-busy {
    background: #8792ad;
    cursor: progress;
}

.btn-primary.is-busy:hover,
.btn-primary.is-busy:focus-visible {
    background: #8792ad;
}

@media (max-width: 360px) {
    .success-title {
        font-size: 22px;
    }

    .shell {
        padding-left: 16px;
        padding-right: 16px;
    }
}
</style>
